import {
  COMPANY_STATUSES,
  fmtRecent,
  isAccountFeeSettled,
  regionLabel,
} from '@remote-medium/shared';
import { unwrap, useApi } from '@remote-medium/ui';
import { Check } from 'lucide-react';
import { Link, useSearchParams } from 'react-router';

import { useBillingEnabled } from '../../lib/billing.tsx';
import { admin } from '../../lib/client.ts';
import { CompanyStatusChip } from '../../lib/status-chip.tsx';
import {
  DataTable,
  EmptyRow,
  LinkButton,
  None,
  Page,
  PageHeader,
  ROW_LINK,
  StatusRow,
  Tabs,
  tableReady,
} from '../../lib/ui.tsx';

type Status = (typeof COMPANY_STATUSES)[number];
const TAB_LABELS: Record<Status, string> = {
  pending: 'Pending',
  active: 'Active',
  suspended: 'Suspended',
};

export function CompaniesPage() {
  const billing = useBillingEnabled();
  const { data, error, loading } = useApi(() => unwrap(admin.companies.$get()), []);
  const [params, setParams] = useSearchParams();
  const status = (params.get('status') as Status | null) ?? null;

  const all = data ?? [];
  const rows = status ? all.filter((c) => c.status === status) : all;
  const ready = tableReady({ data, loading, error });
  const count = (s: Status) => all.filter((c) => c.status === s).length;

  return (
    <Page className="gap-6">
      <PageHeader
        title="Companies"
        description={
          billing
            ? "A company can post once you've done the sanity check and their account fee is paid or waived."
            : "Billing is off: a company can post once you've done the sanity check."
        }
      />

      <Tabs
        label="Filter by status"
        value={status ?? 'all'}
        onChange={(v) => setParams(v === 'all' ? {} : { status: v }, { replace: true })}
        tabs={[
          { value: 'all', label: 'All', count: data ? all.length : undefined },
          ...COMPANY_STATUSES.map((s) => ({
            value: s,
            label: TAB_LABELS[s],
            count: data ? count(s) : undefined,
          })),
        ]}
      />

      <DataTable>
        <thead>
          <tr>
            <th>Company</th>
            <th>Status</th>
            <th>Account fee</th>
            <th>Reviewed</th>
            <th className="text-right">Credits</th>
            <th className="text-right">Open roles</th>
            <th>Joined</th>
            <th>
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <StatusRow colSpan={8} loading={loading} error={error} />
          {ready && rows.length === 0 && (
            <EmptyRow colSpan={8}>
              {status ? `No ${TAB_LABELS[status].toLowerCase()} companies.` : 'No companies yet.'}
            </EmptyRow>
          )}
          {ready &&
            rows.map((c) => {
              // Paused billing (Q65): a company activated earlier with the fee
              // unpaid needs "Mark not owed" from a curator too.
              const needsReview =
                c.status === 'pending' && (!c.activatedAt || (!billing && !isAccountFeeSettled(c)));
              return (
                <tr key={c.id} className="hover:bg-muted/50">
                  <td className="max-w-72">
                    <Link to={`/companies/${c.id}`} className={ROW_LINK}>
                      {c.name}
                    </Link>
                    <div className="truncate text-muted-foreground">
                      {[
                        c.website?.replace(/^https?:\/\//, ''),
                        c.hqCountry && regionLabel(c.hqCountry),
                      ]
                        .filter(Boolean)
                        .join(' · ') || 'No website'}
                    </div>
                  </td>
                  <td>
                    <CompanyStatusChip status={c.status} />
                  </td>
                  <td className="whitespace-nowrap">
                    {c.accountFeePaidAt ? (
                      <span className="inline-flex items-center gap-1">
                        <Check aria-hidden="true" className="size-3.5" /> Paid
                      </span>
                    ) : c.feeWaivedAt ? (
                      'Waived'
                    ) : c.feeExemptAt ? (
                      'Free (launch)'
                    ) : !billing && !c.activatedAt ? (
                      <None>Not needed</None>
                    ) : (
                      <None>Unpaid</None>
                    )}
                  </td>
                  <td className="whitespace-nowrap">
                    {c.activatedAt ? (c.activatedByName ?? 'Done') : <None>Not yet</None>}
                  </td>
                  <td className="text-right tabular-nums">{c.creditsRemaining}</td>
                  <td className="text-right tabular-nums">{c.openRoles}</td>
                  <td className="whitespace-nowrap text-muted-foreground tabular-nums">
                    {fmtRecent(c.createdAt)}
                  </td>
                  <td className="text-right">
                    {needsReview && <LinkButton to={`/companies/${c.id}`}>Review</LinkButton>}
                  </td>
                </tr>
              );
            })}
        </tbody>
      </DataTable>
    </Page>
  );
}
